// ✅ Profile.jsx - Brainy Journey Hub with Share My Progress

import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import toast, { Toaster } from 'react-hot-toast';
import html2canvas from 'html2canvas';
import './Profile.css';

export default function Profile() {
  const [userData, setUserData] = useState(null);
  const [availableAvatars, setAvailableAvatars] = useState([
    { type: 'emoji', value: '🐱', unlockLevel: 0 },
    { type: 'emoji', value: '🐯', unlockLevel: 3 },
    { type: 'emoji', value: '👦', unlockLevel: 5 },
    { type: 'emoji', value: '😀', unlockLevel: 7 },
    { type: 'emoji', value: '🧙‍♂', unlockLevel: 10 },
    { type: 'emoji', value: '🦉', unlockLevel: 15 },
    { type: 'image', value: '/avatars/avatar1.png', unlockLevel: 20 },
    { type: 'image', value: '/avatars/avatar4.png', unlockLevel: 20 },
    { type: 'image', value: '/avatars/avatar2.png', unlockLevel: 30 },
    { type: 'image', value: '/avatars/avatar5.png', unlockLevel: 30 },
    { type: 'image', value: '/avatars/avatar3.png', unlockLevel: 50 },
    { type: 'image', value: '/avatars/avatar6.png', unlockLevel: 50 },
    { type: 'image', value: '/avatars/avatar7.png', unlockLevel: 75 },
    { type: 'image', value: '/avatars/avatar8.png', unlockLevel: 75 },
    { type: 'image', value: '/avatars/avatar9.png', unlockLevel: 100 },
    { type: 'image', value: '/avatars/avatar10.png', unlockLevel: 100 },
    { type: 'image', value: '/avatars/avatar11.png', unlockLevel: 150 },
    { type: 'image', value: '/avatars/avatar12.png', unlockLevel: 150 },
    { type: 'image', value: '/avatars/avatar13.png', unlockLevel: 200 },
    { type: 'image', value: '/avatars/avatar14.png', unlockLevel: 250 }


  ]);
  const [unlockedAvatars, setUnlockedAvatars] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) return;
      const uid = user.uid;

      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return;
      const profile = userSnap.data();

      const lbRef = doc(db, 'leaderboard_master', uid);
      const lbSnap = await getDoc(lbRef);
      const lbData = lbSnap.exists() ? lbSnap.data() : {};

      const merged = {
        name: profile.name,
        email: profile.email,
        chronos: profile.chronos || 0,
        badges: profile.badges || [],
        avatar: profile.avatar || '',
        accessType: profile.accessType || 'demo',
        bestNumberMemoryLevel: lbData.bestNumberMemoryLevel || 0,
        bestFocusGameStreak: lbData.bestFocusGameStreak || 0,
        bestSynonymWords: lbData.bestSynonymWords || 0,
        bestMathSprintScore: lbData.bestMathSprintScore || 0,
        bestLogicScore: lbData.bestLogicScore || 0,
        bestReactionTime: lbData.bestReactionTime || 0,
        joined: user.metadata.creationTime,
        activeDates: profile.activeDates || [],
      };

      setUserData(merged);

      const level = Math.floor(merged.chronos / 100);
      const unlocked = availableAvatars
        .filter(a => level >= a.unlockLevel)
        .map(a => a.value);
      setUnlockedAvatars(unlocked);
    });

    return () => unsubscribe();
  }, [availableAvatars]);

  const handleAvatarSelect = async (val) => {
    if (!unlockedAvatars.includes(val)) return;
    try {
      const ref = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(ref, { avatar: val });
      toast.success('Avatar updated!');
      setUserData(prev => ({ ...prev, avatar: val }));
    } catch {
      toast.error('Failed to update avatar');
    }
  };

  const handleShare = async () => {
    const card = document.querySelector('.profile-summary');
    if (!card) return;
    try {
      const canvas = await html2canvas(card);
      const link = document.createElement('a');
      link.download = 'my_brainy_progress.png';
      link.href = canvas.toDataURL();
      link.click();
      toast.success('🎉 Progress image saved!');
    } catch (err) {
      toast.error('Failed to capture profile card');
      console.error(err);
    }
  };

  const renderBadge = (day, unlocked, date) => (
    <div className="flex flex-col items-center w-20" key={day}>
      <div className="relative w-16 h-16 rounded-xl overflow-hidden shadow-sm badge-box">
        <img
          src={`/assets/badges/${day}day.png`}
          alt={`${day}-day badge`}
          className={`${unlocked ? '' : 'blur-sm grayscale'}`}
        />
        {!unlocked && (
          <div className="absolute inset-0 bg-white/40 flex items-center justify-center text-xs font-semibold text-gray-700">
            Locked
          </div>
        )}
      </div>
      <p className="text-xs mt-1 text-gray-600 text-center">
        {unlocked ? date || 'Unlocked' : '🔒'}
      </p>
    </div>
  );

  if (!userData) return null;
  const {
    name, email, chronos, badges = [], joined,
    avatar, bestNumberMemoryLevel, bestFocusGameStreak, bestSynonymWords, bestMathSprintScore = 0, bestLogicScore = 0, bestReactionTime = 0, accessType
  } = userData;
  const level = Math.floor(chronos / 100);

  const demoLockClass = accessType === 'demo' ? 'opacity-50 pointer-events-none' : '';

  return (
    <>
      <Navbar />
      <Toaster position="top-center" />
      <div className="min-h-screen bg-gradient-to-br from-yellow-100 to-purple-100 p-6">
        <div className="max-w-3xl mx-auto profile-summary bg-white/90 shadow-lg rounded-2xl p-6 space-y-6">

          <h1 className="text-3xl font-bold text-purple-800 border-b-2 pb-2 border-purple-300">
            👤 Profile & Progress
          </h1>

          <div className="flex flex-col sm:flex-row items-center gap-4 justify-between">
            <div className="flex-1 space-y-1 text-purple-800 font-semibold">
              <p className="text-xl">
                Name: <span className="text-purple-700 font-bold">{name}</span>
              </p>
              <p className="text-md">
                Email: <span className="text-purple-600">{email}</span>
              </p>
              <p className="text-md">
                Joined:{' '}
                <span className="text-purple-600">
                  {new Date(joined).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </p>
              <p className="text-md">
                Chronos: <span className="text-yellow-600 font-bold">{chronos}</span>
              </p>
              <p className="text-md">
                Level: <span className="text-green-700 font-bold">{level}</span>
              </p>
            </div>
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full bg-white ring-2 ring-purple-300 shadow-md flex items-center justify-center">
                {avatar && avatar.startsWith('/avatars') ? (
                  <img src={avatar} alt="avatar" className="w-full h-full object-cover rounded-full" />
                ) : (
                  <span className="text-5xl">{avatar || '👶'}</span>
                )}
              </div>
            </div>
          </div>

          {accessType === 'demo' && (
            <div className="bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800 p-4 rounded-lg flex items-center justify-between">
              <span>🔒 Personal Bests & Avatar Customizer are Pro-only features.</span>
              <button onClick={() => navigate('/upgrade')} className="ml-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                🚀 Upgrade
              </button>
            </div>
          )}

          <section className={demoLockClass}>
            <h2 className="text-xl font-bold text-purple-700 mb-2">🏅 Your Streak Badges</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-white border border-purple-200 rounded-xl p-4">
              {[7,21,50,100].map(day => renderBadge(day, badges.includes(day), badges.includes(day) ? 'Unlocked' : null))}
            </div>
          </section>

          <section className={demoLockClass}>
            <h2 className="text-xl font-bold text-purple-700 mb-2">💡 Personal Bests</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-white border border-purple-200 rounded-xl p-4">
              <div className="stat-card">🔢 Max. Digits: {bestNumberMemoryLevel}</div>
              <div className="stat-card">🎯 Focus Streak: {bestFocusGameStreak}</div>
              <div className="stat-card">📚 Synonyms: {bestSynonymWords}</div>
              <div className="stat-card">➕ Math Sprint: {bestMathSprintScore}</div>
              <div className="stat-card">🧠 Logic Score: {bestLogicScore}</div>
              <div className="stat-card">
  ⚡ Reaction Time: {bestReactionTime ? `${bestReactionTime} ms` : '-'}
  {bestReactionTime > 0 && bestReactionTime < 200 && (
    <span className="ml-2 inline-block bg-yellow-300 text-yellow-800 text-xs font-bold px-2 py-1 rounded-full animate-pulse">
      🚀 Top Reflex
    </span>
  )}
</div>
            </div>
          </section>

          <section className={demoLockClass}>
            <h2 className="text-xl font-bold text-purple-700 mb-2">🎨 Choose Your Avatar</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 bg-white border border-purple-200 rounded-xl p-4">
              {availableAvatars.map((a, i) => {
                const isUnlocked = unlockedAvatars.includes(a.value);
                return (
                  <button
                    key={i}
                    onClick={() => isUnlocked && handleAvatarSelect(a.value)}
                    disabled={!isUnlocked}
                    className={`relative flex items-center justify-center w-20 h-20 border-2 rounded-xl bg-white shadow-md transition-all duration-200 ${avatar === a.value ? 'border-purple-500 ring-2 ring-purple-300' : 'border-gray-200'} ${!isUnlocked ? 'opacity-30 cursor-not-allowed' : 'hover:scale-105'}`}
                  >
                    {a.type === 'emoji' ? (
                      <span className="text-3xl">{a.value}</span>
                    ) : (
                      <img src={a.value} alt="avatar" className="w-full h-full object-cover rounded-xl" />
                    )}
                    {!isUnlocked && (
                      <div className="absolute inset-0 bg-white/60 flex items-center justify-center text-sm text-gray-700 font-semibold">
                        🔒
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          <div className="flex justify-center">
            <button
              onClick={handleShare}
              className="px-6 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition"
            >
              📤 Share My Progress
            </button>
          </div>

        </div>
      </div>
    </>
  );
}
